import { useState } from "react";
import Input from "../../../components/common/input/Input";
import Textarea from "../../../components/common/textarea/Textarea";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import styles from "./CreateProduct.module.scss";
import Button from "../../../components/common/button/Button";
import {
  AtributesTypes,
  CreateProductVariantType,
  ErrorsType,
  ProductsVariantType,
} from "../../../api/types/products.types";
import ProductsService from "../../../api/services/products.service";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { toast } from "sonner";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import delete_icon from "../../../assets/images/actions/delete.svg";
import DeleteModal from "../../../components/common/delete-modal/DeleteModal";
import DynamicAttributeField from "../../../components/common/dynamic-attribute/DynamicAttributeField";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const CreateVariants = () => {
  const { t } = useTranslation("translation");
  const { id } = useParams();
  const location = useLocation();
  const { category_id } = location.state || {};
  const [variantCreated, setVariantCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<
    Partial<CreateProductVariantType>
  >({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [errors, setErrors] = useState<Partial<ErrorsType>>({});
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const getAttributeName = (attr: AtributesTypes): string => {
    const lang = i18n.language;
    switch (lang) {
      case "az":
        return attr.name_az || attr.name;
      case "en":
        return attr.name_en || attr.name;
      case "es":
        return attr.name_es || attr.name;
      case "de":
        return attr.name_de || attr.name;
      default:
        return attr.name;
    }
  };

  const getAttributeValues = (attr: AtributesTypes) => {
    const lang = i18n.language;
    const valuesMap: Record<string, any[]> = {
      az: attr.values_az || attr.values,
      en: attr.values_en || attr.values,
      es: attr.values_es || attr.values,
      de: attr.values_de || attr.values,
    };

    const valuesArray = valuesMap[lang] || attr.values;
    return (
      valuesArray?.map((v) => ({
        id: v.id || v.name,
        name: v.name,
      })) || []
    );
  };

  const { data: existingVariants, reset: refetchVariants } =
    useInfiniteScroll<ProductsVariantType>({
      fetchFn: async (page) => {
        if (!id) return [];
        const res = await ProductsService.getAllVariants(Number(id), page, 20);
        return res.data.data || [];
      },
      pageSize: 20,
    });

  const { data: atributes } = useInfiniteScroll<AtributesTypes>({
    fetchFn: async (page) => {
      if (!category_id) return [];
      const res = await ProductsService.getCategoriesAttribute(
        Number(category_id),
        page,
        20,
      );
      return res.data;
    },
    pageSize: 20,
    enabled: Boolean(category_id),
    deps: [category_id],
  });

  const allVariants = [...(existingVariants || [])];

  const validateForm = (): boolean => {
    const newErrors: Partial<ErrorsType> = {};

    if (!formValues.name?.trim()) newErrors.name = t("required.name") || "";
    if (!formValues.description?.trim())
      newErrors.description = t("required.description") || "";
    if (!formValues.sku?.trim()) newErrors.sku = t("required.sku") || "";

    if (!formValues.stock_quantity) {
      newErrors.stock_quantity = t("required.stock_quantity") || "";
    }
    if (!formValues.price || Number(formValues.price) <= 0)
      newErrors.price = t("required.price") || "";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", formValues.name!);
      formData.append("description", formValues.description!);
      formData.append("sku", formValues.sku!);
      formData.append("stock_quantity", String(formValues.stock_quantity));
      formData.append("price", String(formValues.price));
      formData.append(
        "cost_price",
        formValues.cost_price ? String(formValues.cost_price) : "0",
      );
      formData.append(
        "discount_percent",
        formValues.discount_percent ? String(formValues.discount_percent) : "0",
      );
      formData.append("product_id", String(id));
      if (formValues.json_data) {
        formData.append("json_data", JSON.stringify(formValues.json_data));
      }
      await ProductsService.createProductVariant(formData);
      setVariantCreated(true);
      toast.success(t("products_details.variant_created"), {
        description: t("products_details.variant_created_desc"),
      });
      refetchVariants();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateProductVariantType, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const resetForm = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setFormValues({});
    setErrors({});
    setVariantCreated(false);
  };

  const handleDelete = async (var_id: number) => {
    try {
      await ProductsService.deleteProductVariant(var_id, Number(id));
      setDeleteModal(false);
      refetchVariants();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    }
  };

  const discountedPrice =
    formValues.price && formValues.discount_percent
      ? Number(formValues.price) -
        (Number(formValues.price) * Number(formValues.discount_percent)) / 100
      : null;

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("add_variants")} />
      {allVariants.length > 0 && (
        <div className={styles.continue_section}>
          <div className={styles.continue_info}>
            <p>
              <strong>{allVariants.length}</strong>{" "}
              {t("products_details.varinat_created")}{" "}
              {t("products_details.continue_images")}
            </p>
          </div>
          <div className={styles.variants_list}>
            <div className={styles.variants_grid}>
              {allVariants.map((variant, index) => (
                <div
                  key={variant.id || `new-${index}`}
                  className={styles.variant_card}
                >
                  <div
                    className={styles.delete_icon}
                    onClick={() => {
                      setDeleteModal(true);
                      setDeleteId(variant.id);
                    }}
                  >
                    <img src={delete_icon} alt="del" />
                  </div>
                  <div className={styles.variant_info}>
                    <h4>
                      {variant.name_en ||
                        variant.name_az ||
                        variant.name_de ||
                        variant.name_es ||
                        t("products_details.unnamed_variant")}
                    </h4>
                    <p>
                      <strong>{t("columns.sku")}:</strong> {variant.sku}
                    </p>
                    <p>
                      <strong>{t("columns.price")}:</strong>
                      {variant.price}
                    </p>
                    <p>
                      <strong>{t("columns.stock")}:</strong>{" "}
                      {variant.stock_quantity}
                    </p>
                    <p>
                      {variant.attributes.map((el) => (
                        <p>
                          <strong>{el.attribute_name}:</strong> {el.value}
                        </p>
                      ))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.form_header}>
          <h3>
            {variantCreated
              ? t("products_details.add_another_variant")
              : t("products_details.add_new_variants")}
          </h3>
        </div>

        <div className={styles.grid}>
          <div className={styles.left}>
            <div className={styles.form_category}>
              <p className={styles.title}>
                {t("products_details.product_info")}
              </p>
              <div className={styles.form_content_column}>
                <Input
                  className={styles.input}
                  label={t("labels.name") || ""}
                  placeholder={t("labels.name") || ""}
                  value={formValues?.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  error={errors.name}
                />
                <Textarea
                  className={styles.input}
                  label={t("labels.description") || ""}
                  placeholder={t("labels.description") || ""}
                  value={formValues.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  error={errors.description}
                  maxLength={500}
                  showCount
                />
                <Input
                  className={styles.input}
                  label={t("labels.sku") || ""}
                  placeholder="eg. 242425252"
                  value={formValues.sku || ""}
                  onChange={(e) => handleChange("sku", e.target.value)}
                  error={errors.sku}
                />
                <Input
                  className={styles.input}
                  label={t("labels.stock_quantity") || ""}
                  placeholder={t("labels.stock_quantity") || ""}
                  type="number"
                  value={formValues.stock_quantity || ""}
                  onChange={(e) =>
                    handleChange("stock_quantity", e.target.value)
                  }
                  error={errors.stock_quantity}
                />
              </div>
            </div>
          </div>

          <div className={styles.right}>
            <div className={styles.form_category}>
              <p className={styles.title}>{t("products_details.pricing")}</p>
              <div className={styles.form_content_column}>
                <Input
                  className={styles.input}
                  label={t("labels.cost_price") || ""}
                  type="number"
                  placeholder={t("labels.cost_price") || ""}
                  value={formValues?.cost_price || ""}
                  onChange={(e) => handleChange("cost_price", e.target.value)}
                />
                <p className={styles.info}>{t("labels.cost_price_hint")}</p>
                <Input
                  className={styles.input}
                  label={t("labels.price") || ""}
                  type="number"
                  placeholder={t("labels.price") || ""}
                  value={formValues?.price || ""}
                  onChange={(e) => handleChange("price", e.target.value)}
                  error={errors.price}
                />
                <Input
                  className={styles.input}
                  label={t("labels.discount_percent") || ""}
                  type="number"
                  placeholder={t("labels.discount_percent") || ""}
                  value={formValues?.discount_percent || ""}
                  onChange={(e) =>
                    handleChange("discount_percent", e.target.value)
                  }
                />
                {discountedPrice !== null && (
                  <p className={styles.discounted}>
                    {t("labels.discounted_price")}:{" "}
                    <strong>{discountedPrice}</strong>
                  </p>
                )}
              </div>
            </div>

            <div className={styles.form_category}>
              <p className={styles.title}>{t("products_details.attributes")}</p>
              <div className={styles.form_content_column}>
                {atributes.length > 0 &&
                  atributes.map((attr) => (
                    <DynamicAttributeField
                      key={attr.id}
                      attribute={{
                        ...attr,
                        name: getAttributeName(attr),
                        values: getAttributeValues(attr),
                      }}
                      value={
                        formValues.json_data?.find(
                          (i) => i.attribute_id === attr.id,
                        )?.value || ""
                      }
                      // error={errors[`attribute_${attr.id}` as keyof ErrorsType]}
                      onChange={(attribute_id, val) => {
                        setFormValues((prev) => {
                          const existing = prev.json_data
                            ? [...prev.json_data]
                            : [];
                          const index = existing.findIndex(
                            (i) => i.attribute_id === attribute_id,
                          );
                          const newItem = { attribute_id, value: val };
                          if (index >= 0) existing[index] = newItem;
                          else existing.push(newItem);
                          return { ...prev, json_data: existing };
                        });
                        setErrors((prev) => ({
                          ...prev,
                          [`attribute_${attr.id}`]: "",
                        }));
                      }}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>

        {!variantCreated ? (
          <div className={styles.other_btns}>
            <Button type="submit" disabled={loading}>
              {loading ? t("buttons.loading") : t("buttons.create_variant")}
            </Button>
          </div>
        ) : (
          <div className={styles.other_btns}>
            <Button
              type="button"
              onClick={resetForm}
              className={styles.add_variant}
            >
              {t("products_details.add_another_variant")}
            </Button>
            <Button
              type="button"
              onClick={() =>
                navigate(`/dashboard/products/create-variant-images/${id}`)
              }
            >
              {t("buttons.continue_to_images")}
            </Button>
          </div>
        )}
      </form>
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("products_details.delete_variant") || ""}
      />
    </div>
  );
};

export default CreateVariants;
